/**
 * Exception Escalation Engine (Deterministic)
 *
 * Pure logic — no side effects, no API calls.
 * Scans today's schedule, active occupants, and docks to produce
 * a priority-sorted list of operational exceptions requiring attention.
 *
 * Exception types (v1):
 *   1. CRITICAL_DWELL  — occupied dock > SLA_CRITICAL_MINUTES
 *   2. NO_DOCK_SOON    — approved/scheduled with no dock, starts within 60m
 *   3. STACKING_SOON   — 3+ arrivals within 45m on same dock, next 90m
 *   4. DETENTION_RISK   — carrier at risk of detention in next 6h
 */
import type { Appointment } from '../types/appointments';
import { SLA_CRITICAL_MINUTES } from './sla';
import { safeDateMs } from './cascadePrediction';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Appointments starting within this window trigger NO_DOCK exceptions. */
const NO_DOCK_HORIZON_MINUTES = 60;

/** Minimum arrivals in a 45m window to trigger a stacking exception. */
const STACKING_MIN_ARRIVALS = 3;
const STACKING_WINDOW_MS = 45 * 60 * 1000;
const STACKING_HORIZON_MS = 90 * 60 * 1000;

/** Free-time window before detention begins (mirrors ProjectedDetentionExposure). */
const DETENTION_FREE_MINUTES = 120;
const DETENTION_HORIZON_MINUTES = 360;

/** Max exceptions shown in the panel. */
export const MAX_VISIBLE_EXCEPTIONS = 5;

// ─── Types ────────────────────────────────────────────────────────────────────

export type ExceptionType = 'CRITICAL_DWELL' | 'NO_DOCK_SOON' | 'STACKING_SOON' | 'DETENTION_RISK';

/** Priority order (lower = higher priority). */
const TYPE_PRIORITY: Record<ExceptionType, number> = {
    CRITICAL_DWELL: 0,
    NO_DOCK_SOON: 1,
    STACKING_SOON: 2,
    DETENTION_RISK: 3,
};

export interface ExceptionItem {
    type: ExceptionType;
    /** Human-readable label, e.g. "CRITICAL" or "NO DOCK". */
    badge: string;
    /** Main message copy. */
    message: string;
    /** Urgency value used for secondary sort (higher = more urgent). */
    urgency: number;
    /** Action label for the button. */
    actionLabel: string;
    /** Action payload — what to open. */
    action:
    | { kind: 'openDock'; dockId: string }
    | { kind: 'openAppointment'; appointment: Appointment }
    | { kind: 'openRecovery' }
    | { kind: 'viewExposure' };
}

export interface ExceptionResult {
    items: ExceptionItem[];
    totalCount: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDwellCompact(minutes: number): string {
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
}

function formatMinutesUntil(ms: number, now: number): string {
    const diff = ms - now;
    const minutes = Math.round(diff / 60000);
    if (minutes <= 0) return 'now';
    if (minutes < 60) return `in ${minutes}m`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (m === 0) return `in ${h}h`;
    return `in ${h}h ${m}m`;
}

import { formatTimeInTimeZone } from './timezoneDisplay';
import { useAuthStore } from '../hooks/useAuth';

function formatTimeCompact(ms: number, warehouseTimeZone?: string): string {
    return formatTimeInTimeZone(new Date(ms).toISOString(), warehouseTimeZone);
}

// ─── Detection Functions ──────────────────────────────────────────────────────

function detectCriticalDwell(
    activeOccupants: Appointment[],
    docks: { id: string; dockNumber: string }[],
    now: number,
): ExceptionItem[] {
    const dockMap = new Map(docks.map(d => [d.id, d.dockNumber]));
    const items: ExceptionItem[] = [];

    for (const occ of activeOccupants) {
        if (!occ.checkInTime) continue;
        const checkInMs = safeDateMs(occ.checkInTime);
        if (isNaN(checkInMs)) continue;

        const dwellMinutes = (now - checkInMs) / 60000;
        if (dwellMinutes <= SLA_CRITICAL_MINUTES) continue;

        const dockId = occ.dockId || occ.dock_id;
        if (!dockId) continue;

        const dockNum = dockMap.get(dockId) || dockId;

        items.push({
            type: 'CRITICAL_DWELL',
            badge: 'CRITICAL',
            message: `Dock ${dockNum} dwell ${formatDwellCompact(dwellMinutes)}`,
            urgency: dwellMinutes,
            actionLabel: 'Open Dock',
            action: { kind: 'openDock', dockId },
        });
    }

    return items;
}

function detectNoDockSoon(
    todaySchedule: Appointment[],
    now: number,
): ExceptionItem[] {
    const horizonMs = now + NO_DOCK_HORIZON_MINUTES * 60 * 1000;
    const items: ExceptionItem[] = [];

    for (const appt of todaySchedule) {
        const status = (appt.status || '').toLowerCase();
        if (status !== 'approved' && status !== 'scheduled') continue;

        const hasDock = !!(appt.dockId || appt.dock_id);
        if (hasDock) continue;

        const startMs = safeDateMs(appt.startAt);
        if (isNaN(startMs)) continue;
        if (startMs > horizonMs || startMs < now - 30 * 60 * 1000) continue; // skip far past

        const poLabel = appt.customerPoNumber || 'Unknown PO';
        const carrierSuffix = appt.carrierName ? ` — ${appt.carrierName}` : '';

        items.push({
            type: 'NO_DOCK_SOON',
            badge: 'NO DOCK',
            message: `PO ${poLabel} starts ${formatMinutesUntil(startMs, now)}${carrierSuffix}`,
            urgency: horizonMs - startMs, // closer = higher urgency
            actionLabel: 'Open Appointment',
            action: { kind: 'openAppointment', appointment: appt },
        });
    }

    return items;
}

function detectStackingSoon(
    todaySchedule: Appointment[],
    docks: { id: string; dockNumber: string }[],
    now: number,
    warehouseTimeZone?: string
): ExceptionItem[] {
    const cutoff = now + STACKING_HORIZON_MS;
    const dockMap = new Map(docks.map(d => [d.id, d.dockNumber]));
    const items: ExceptionItem[] = [];

    // Group upcoming appointments by dock
    const byDock = new Map<string, number[]>();

    for (const appt of todaySchedule) {
        const status = (appt.status || '').toLowerCase();
        if (status !== 'approved' && status !== 'scheduled') continue;

        const did = appt.dockId || appt.dock_id;
        if (!did) continue;

        const t = safeDateMs(appt.startAt);
        if (isNaN(t) || t < now || t > cutoff) continue;

        const list = byDock.get(did) || [];
        list.push(t);
        byDock.set(did, list);
    }

    // Find docks with 3+ arrivals in a 45m window
    for (const [dockId, times] of byDock) {
        if (times.length < STACKING_MIN_ARRIVALS) continue;
        times.sort((a, b) => a - b);

        for (let i = 0; i <= times.length - STACKING_MIN_ARRIVALS; i++) {
            // Count how many fit in the window starting from times[i]
            let count = 1;
            for (let j = i + 1; j < times.length; j++) {
                if (times[j] - times[i] <= STACKING_WINDOW_MS) {
                    count++;
                } else {
                    break;
                }
            }

            if (count >= STACKING_MIN_ARRIVALS) {
                const windowStart = times[i];
                const windowEnd = times[Math.min(i + count - 1, times.length - 1)];
                const dockNum = dockMap.get(dockId) || dockId;

                items.push({
                    type: 'STACKING_SOON',
                    badge: 'STACKING',
                    message: `${count} arrivals on Dock ${dockNum} ${formatTimeCompact(windowStart, warehouseTimeZone)}-${formatTimeCompact(windowEnd, warehouseTimeZone)}`,
                    urgency: cutoff - windowStart,
                    actionLabel: 'Open Recovery',
                    action: { kind: 'openRecovery' },
                });
                break; // One exception per dock
            }
        }
    }

    return items;
}

function detectDetentionRisk(
    activeOccupants: Appointment[],
    now: number,
): ExceptionItem[] {
    // Compute per-carrier risk (mirrors ProjectedDetentionExposure logic)
    const carrierRisk = new Map<string, { label: string; count: number }>();

    for (const occ of activeOccupants) {
        if (!occ.checkInTime) continue;
        const checkInMs = safeDateMs(occ.checkInTime);
        if (isNaN(checkInMs)) continue;

        const dwellMinutes = (now - checkInMs) / 60000;
        const projectedDwell = dwellMinutes + DETENTION_HORIZON_MINUTES;
        const projectedOver = Math.max(0, projectedDwell - DETENTION_FREE_MINUTES);
        const currentOver = Math.max(0, dwellMinutes - DETENTION_FREE_MINUTES);
        const incrementalOver = Math.max(0, projectedOver - currentOver);

        if (incrementalOver <= 0) continue;

        const carrierKey = occ.carrierId || `${occ.carrierName || ''}_${occ.carrierMcNumber || ''}`.toLowerCase().trim();
        const carrierLabel = occ.carrierName || `MC ${occ.carrierMcNumber || '?'}`;

        const existing = carrierRisk.get(carrierKey);
        if (existing) {
            existing.count++;
        } else {
            carrierRisk.set(carrierKey, { label: carrierLabel, count: 1 });
        }
    }

    if (carrierRisk.size === 0) return [];

    const items: ExceptionItem[] = [];
    const count = carrierRisk.size;

    items.push({
        type: 'DETENTION_RISK',
        badge: 'RISK',
        message: `${count} carrier${count !== 1 ? 's' : ''} at detention risk in next 6h`,
        urgency: count,
        actionLabel: 'View Exposure',
        action: { kind: 'viewExposure' },
    });

    return items;
}

// ─── Main Engine ──────────────────────────────────────────────────────────────

export function computeExceptions(
    todaySchedule: Appointment[],
    activeOccupants: Appointment[],
    docks: { id: string; dockNumber: string }[],
    now: number,
    warehouseTimeZone?: string
): ExceptionResult {
    const allItems: ExceptionItem[] = [
        ...detectCriticalDwell(activeOccupants, docks, now),
        ...detectNoDockSoon(todaySchedule, now),
        ...detectStackingSoon(todaySchedule, docks, now, warehouseTimeZone),
        ...detectDetentionRisk(activeOccupants, now),
    ];

    // Deduplicate by composite key (type + dockId/appointmentId)
    const seen = new Set<string>();
    const deduped = allItems.filter(item => {
        let key = item.type;
        if (item.action.kind === 'openDock') key += `-${item.action.dockId}`;
        else if (item.action.kind === 'openAppointment') key += `-${item.action.appointment.id}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });

    // Sort by type priority, then within same type by urgency (descending)
    deduped.sort((a, b) => {
        const pa = TYPE_PRIORITY[a.type];
        const pb = TYPE_PRIORITY[b.type];
        if (pa !== pb) return pa - pb;
        return b.urgency - a.urgency;
    });

    return {
        items: deduped.slice(0, MAX_VISIBLE_EXCEPTIONS),
        totalCount: deduped.length,
    };
}
